import { useState, useEffect } from 'react';
import api, { handleApiError, queryParams } from '../services/api';
import { formatCurrency, formatDate, statusBadgeClass, statusLabel } from '../utils/formatters';
import toast from 'react-hot-toast';
import {
  CreditCard,
  Search,
  ChevronLeft,
  ChevronRight,
  X,
  DollarSign,
  Clock,
  RefreshCw,
  User,
} from 'lucide-react';

function ReceivablesRow({ receivable, onReceive }) {
  return (
    <tr>
      <td>
        <div className="flex items-center gap-2">
          <User className="h-4 w-4 text-gray-400" />
          <span className="text-sm text-gray-700 dark:text-gray-200">{receivable.customer?.name || receivable.customer_name || '-'}</span>
        </div>
      </td>
      <td>
        <span className="text-sm font-medium text-gray-900 dark:text-white">{receivable.invoice_number || receivable.invoice_no || '-'}</span>
      </td>
      <td>
        <span className="text-sm text-gray-700 dark:text-gray-200">{formatDate(receivable.invoice_date || receivable.date)}</span>
      </td>
      <td>
        <span className="text-sm text-gray-700 dark:text-gray-200">{formatDate(receivable.due_date)}</span>
      </td>
      <td className="text-right">
        <span className="text-sm text-gray-700 dark:text-gray-200">{formatCurrency(receivable.original_amount || receivable.amount)}</span>
      </td>
      <td className="text-right">
        <span className="text-sm font-medium text-gray-900 dark:text-white">{formatCurrency(receivable.remaining_balance || receivable.remaining)}</span>
      </td>
      <td>
        <span className={`badge ${getAgingBadge(receivable.aging_days || receivable.aging)}`}>
          {receivable.aging_days ?? receivable.aging ?? 0} hari
        </span>
      </td>
      <td>
        <span className={`badge ${statusBadgeClass(receivable.status)}`}>{statusLabel(receivable.status)}</span>
      </td>
      <td>
        <div className="flex items-center gap-2">
          <button
            onClick={() => onReceive(receivable)}
            className="p-1.5 text-success-600 hover:bg-success-50 dark:hover:bg-success-900/20 rounded"
            title="Terima Pembayaran"
          >
            <DollarSign className="h-4 w-4" />
          </button>
        </div>
      </td>
    </tr>
  );
}

function getAgingTone(days) {
  if (days == null) return 'stat-icon-neutral';
  if (days <= 0) return 'stat-icon-success';
  if (days <= 60) return 'stat-icon-warning';
  return 'stat-icon-danger';
}

function getAgingBadge(days) {
  if (days == null) return 'badge-gray';
  if (days <= 0) return 'badge-success';
  if (days <= 30) return 'badge-warning';
  if (days <= 60) return 'badge-warning';
  if (days <= 90) return 'badge-danger';
  return 'badge-danger';
}

function AgingGroup({ label, days, amount, count }) {
  return (
    <div className="stat-card">
      <div className={`stat-icon ${getAgingTone(days)}`}>
        <Clock className="h-6 w-6" />
      </div>
      <div className="flex-1">
        <p className="stat-label">{label}</p>
        <p className="stat-value">{formatCurrency(amount)}</p>
        <p className="text-xs text-gray-400">{count} invoice</p>
      </div>
    </div>
  );
}

export default function ReceivablesPage() {
  const [receivables, setReceivables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, totalPages: 0 });
  const [filters, setFilters] = useState({ search: '', customer_id: '', status: '' });
  const [customers, setCustomers] = useState([]);
  const [agingSummary, setAgingSummary] = useState(null);

  // Receive payment modal
  const [showReceiveModal, setShowReceiveModal] = useState(false);
  const [selectedReceivable, setSelectedReceivable] = useState(null);
  const [receiveForm, setReceiveForm] = useState({ amount: '', payment_date: '', payment_method: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchReceivables();
    fetchCustomers();
  }, []);

  useEffect(() => {
    fetchReceivables();
  }, [pagination.page]);

  const fetchReceivables = async () => {
    setLoading(true);
    try {
      const params = queryParams({ page: pagination.page, limit: pagination.limit, ...filters });
      const res = await api.get('/accounts-receivable', { params });
      if (res.data.success) {
        const data = res.data.data;
        setReceivables(data?.receivables || data?.data || data || []);
        setPagination((prev) => ({
          ...prev,
          total: data?.total || data?.pagination?.total || 0,
          totalPages: data?.totalPages || data?.pagination?.totalPages || Math.ceil((data?.total || 0) / prev.limit),
        }));
        if (data?.aging_summary || data?.aging) {
          setAgingSummary(data.aging_summary || data.aging);
        }
      }
    } catch (error) {
      handleApiError(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCustomers = async () => {
    try {
      const res = await api.get('/customers', { params: { limit: 100 } });
      if (res.data.success) {
        setCustomers(res.data.data?.customers || res.data.data || []);
      }
    } catch {
      // non-critical
    }
  };

  const handleFilter = () => {
    setPagination((prev) => ({ ...prev, page: 1 }));
    fetchReceivables();
  };

  const resetFilters = () => {
    setFilters({ search: '', customer_id: '', status: '' });
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const openReceiveModal = (receivable) => {
    setSelectedReceivable(receivable);
    setReceiveForm({
      amount: receivable.remaining_balance || receivable.remaining || '',
      payment_date: new Date().toISOString().split('T')[0],
      payment_method: '',
    });
    setShowReceiveModal(true);
  };

  const handleReceive = async (e) => {
    e.preventDefault();
    if (!selectedReceivable) return;
    setSaving(true);
    try {
      const res = await api.post(`/accounts-receivable/${selectedReceivable.id}/receive`, receiveForm);
      if (res.data.success) {
        toast.success('Pembayaran berhasil diterima');
        setShowReceiveModal(false);
        setSelectedReceivable(null);
        fetchReceivables();
      }
    } catch (error) {
      handleApiError(error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="page-title">Piutang Usaha</h1>
          <p className="page-subtitle">Kelola piutang usaha dari pelanggan</p>
        </div>
      </div>

      {/* Aging Summary */}
      {agingSummary && !loading && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <AgingGroup label="0 - 30 Hari" days={15} amount={agingSummary['0-30'] || agingSummary.range_0_30 || 0} count={agingSummary['0-30_count'] || 0} />
          <AgingGroup label="31 - 60 Hari" days={45} amount={agingSummary['31-60'] || agingSummary.range_31_60 || 0} count={agingSummary['31-60_count'] || 0} />
          <AgingGroup label="61 - 90 Hari" days={75} amount={agingSummary['61-90'] || agingSummary.range_61_90 || 0} count={agingSummary['61-90_count'] || 0} />
          <AgingGroup label="90+ Hari" days={95} amount={agingSummary['90+'] || agingSummary.range_90_plus || 0} count={agingSummary['90+_count'] || 0} />
        </div>
      )}

      {/* Filters */}
      <div className="card">
        <div className="card-body">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                className="input pl-9"
                placeholder="Cari invoice..."
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                onKeyDown={(e) => e.key === 'Enter' && handleFilter()}
              />
            </div>
            <div>
              <select className="input" value={filters.customer_id} onChange={(e) => setFilters({ ...filters, customer_id: e.target.value })}>
                <option value="">Semua Pelanggan</option>
                {customers.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>
            <div>
              <select className="input" value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })}>
                <option value="">Semua Status</option>
                <option value="unpaid">Belum Dibayar</option>
                <option value="partial">Sebagian</option>
                <option value="paid">Lunas</option>
                <option value="overdue">Jatuh Tempo</option>
              </select>
            </div>
            <div className="flex gap-2">
              <button onClick={handleFilter} className="btn-primary btn-sm flex-1">
                <Search className="h-4 w-4 mr-1" /> Cari
              </button>
              <button onClick={resetFilters} className="btn-secondary btn-sm">
                <RefreshCw className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="card">
        {loading ? (
          <div className="card-body">
            <div className="space-y-3">
              {[...Array(8)].map((_, i) => (
                <div key={i} className="flex gap-4 items-center">
                  <div className="skeleton h-4 w-36" />
                  <div className="skeleton h-4 w-24" />
                  <div className="skeleton h-4 w-28" />
                  <div className="skeleton h-4 w-28" />
                  <div className="skeleton h-4 w-32" />
                  <div className="skeleton h-4 w-32" />
                  <div className="skeleton h-4 w-20" />
                  <div className="skeleton h-4 w-20" />
                  <div className="skeleton h-4 w-20" />
                </div>
              ))}
            </div>
          </div>
        ) : receivables.length === 0 ? (
          <div className="card-body py-16 text-center">
            <div className="w-16 h-16 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center mx-auto mb-4">
              <CreditCard className="h-8 w-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-1">Tidak Ada Piutang</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">Belum ada piutang usaha yang tercatat.</p>
          </div>
        ) : (
          <>
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th>Pelanggan</th>
                    <th>No. Invoice</th>
                    <th>Tgl. Invoice</th>
                    <th>Jatuh Tempo</th>
                    <th className="text-right">Nilai Awal</th>
                    <th className="text-right">Sisa</th>
                    <th>Aging</th>
                    <th>Status</th>
                    <th>Aksi</th>
                  </tr>
                </thead>
                <tbody>
                  {receivables.map((r) => (
                    <ReceivablesRow key={r.id} receivable={r} onReceive={openReceiveModal} />
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination.totalPages > 1 && (
              <div className="px-6 py-4 border-t border-gray-100 dark:border-gray-700 flex items-center justify-between">
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Menampilkan {((pagination.page - 1) * pagination.limit) + 1} - {Math.min(pagination.page * pagination.limit, pagination.total)} dari {pagination.total} data
                </p>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setPagination((prev) => ({ ...prev, page: Math.max(1, prev.page - 1) }))}
                    disabled={pagination.page <= 1}
                    className="btn btn-sm btn-outline"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </button>
                  <span className="text-sm text-gray-700 dark:text-gray-300 px-2">
                    Halaman {pagination.page} / {pagination.totalPages}
                  </span>
                  <button
                    onClick={() => setPagination((prev) => ({ ...prev, page: Math.min(prev.totalPages, prev.page + 1) }))}
                    disabled={pagination.page >= pagination.totalPages}
                    className="btn btn-sm btn-outline"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Receive Payment Modal */}
      {showReceiveModal && selectedReceivable && (
        <div className="modal-overlay" onClick={() => setShowReceiveModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="px-6 py-4 border-b border-surface-100 dark:border-surface-800 flex items-center justify-between">
              <h3 className="text-base font-semibold text-surface-900 dark:text-white">Penerimaan Pembayaran</h3>
              <button onClick={() => setShowReceiveModal(false)} className="p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700">
                <X className="h-5 w-5 text-gray-500" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              {/* Info */}
              <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500 dark:text-gray-400">Pelanggan</span>
                  <span className="font-medium text-gray-900 dark:text-white">{selectedReceivable.customer?.name || selectedReceivable.customer_name || '-'}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500 dark:text-gray-400">Invoice</span>
                  <span className="font-medium text-gray-900 dark:text-white">{selectedReceivable.invoice_number || selectedReceivable.invoice_no || '-'}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500 dark:text-gray-400">Sisa Piutang</span>
                  <span className="font-medium text-gray-900 dark:text-white">{formatCurrency(selectedReceivable.remaining_balance || selectedReceivable.remaining)}</span>
                </div>
              </div>

              <form onSubmit={handleReceive} className="space-y-4">
                <div>
                  <label className="label">Jumlah Diterima <span className="text-danger-600">*</span></label>
                  <input
                    type="number"
                    className="input"
                    value={receiveForm.amount}
                    onChange={(e) => setReceiveForm({ ...receiveForm, amount: e.target.value })}
                    required
                    min="1"
                    max={selectedReceivable.remaining_balance || selectedReceivable.remaining}
                    placeholder="Masukkan jumlah"
                  />
                </div>
                <div>
                  <label className="label">Tanggal Terima <span className="text-danger-600">*</span></label>
                  <input
                    type="date"
                    className="input"
                    value={receiveForm.payment_date}
                    onChange={(e) => setReceiveForm({ ...receiveForm, payment_date: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <label className="label">Metode Pembayaran</label>
                  <select
                    className="input"
                    value={receiveForm.payment_method}
                    onChange={(e) => setReceiveForm({ ...receiveForm, payment_method: e.target.value })}
                  >
                    <option value="">Pilih Metode</option>
                    <option value="cash">Tunai</option>
                    <option value="bank_transfer">Transfer Bank</option>
                    <option value="check">Cek</option>
                    <option value="giro">Giro</option>
                    <option value="other">Lainnya</option>
                  </select>
                </div>
                <div className="mt-6 flex gap-3 border-t border-surface-100 pt-4 dark:border-surface-800">
                  <button type="button" onClick={() => setShowReceiveModal(false)} className="btn-secondary flex-1">Batal</button>
                  <button type="submit" disabled={saving} className="btn-success flex-1">
                    {saving ? 'Memproses...' : 'Terima Pembayaran'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
